// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import { jsx, PanelLifecycleContext, ParamComponent } from 'panel';
import * as React from 'react';
import ReactDOM from 'react-dom/client';
import { VNode } from 'snabbdom';
import type { JsxVNodeProps } from 'snabbdom-jsx-lite';
import { IronContextRegistry } from 'common/report/context-registry';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { SegmentFilterControllerProvider } from 'common/contexts/react/segment-filter-controller-context';
import { ReactErrorBoundary } from 'common/components/react-error-boundary';
import { ReportBookmarkManagerProvider } from 'common/report/insights/contexts/report-bookmark-manager/provider';
import { getReactComponentId, Providers } from './react';
interface Params {
  props: any;
}
interface JsxProps {
  params: Params;
}

/**
 * These are contexts that react2panel is allowed to subscribe to.
 * DO only add legacy local Panel contexts here. The goal is to not rely on this method to subscribe to contexts and use React Hooks where possible.
 * DO NOT add any "root" level Panel contexts. Those should be subscribed to via hooks in the React component.
 */
const AllowedContexts = {
  segmentFilterController: SegmentFilterControllerProvider,
  reportBookmark: ReportBookmarkManagerProvider,
  urlManager: null
} as const satisfies Partial<Record<keyof IronContextRegistry, React.ComponentType<React.ProviderProps<any> | null>>>;
interface Options<Contexts extends Array<keyof typeof AllowedContexts>> {
  /** Contexts to subscribe to. */
  contexts?: Contexts;
  /** Optional styles to add to the wrapper "content" div */
  wrapperStyles?: Record<string, string>;
  /**
   * Use the shadow dom.
   *
   * WARNING: Do not disable this unless you have removed host styles from your component. It might affect other components!
   */
  useShadowDom?: boolean;
}
interface PanelProps {
  styleOverride?: string;
}
class EmptyPanelContext implements PanelLifecycleContext {
  bindToComponent(_component: any): void {}
  unbindFromComponent(_component: any): void {}
}

/**
 * Wraps a React component with a Panel component.
 */
export function react2panel<Component extends React.JSXElementConstructor<any>, Contexts extends Array<keyof typeof AllowedContexts>>(name: string, reactComponent: Component, opts?: Options<Contexts>): (props: Pick<JsxVNodeProps, `sel` | `hook` | `class`> & PanelProps & Omit<React.ComponentPropsWithRef<Component>, Contexts[number]>) => VNode {
  const componentId = (reactComponent as any).componentId || getReactComponentId();
  class ReactRoot extends ParamComponent<Params, unknown, IronContextRegistry> {
    declare private rootEl: HTMLElement;
    declare private reactRoot: ReactDOM.Root;
    get config() {
      return {
        params: {
          props: Object
        },
        ...(opts?.contexts ? {
          contexts: opts.contexts,
          // we want to allow React components where there's an undefined Panel context up in the DOM tree
          defaultContexts: Object.fromEntries(opts.contexts.map(context => [context, new EmptyPanelContext()]))
        } : {}),
        useShadowDom: opts?.useShadowDom,
        template: () => <div style={{
          // make sure the root isn't causing any styling changes
          display: `contents`,
          ...(opts?.wrapperStyles ? opts.wrapperStyles : {})
        }} hook={{
          insert: vnode => {
            this.rootEl = vnode.elm as HTMLElement;
          }
        }} />,
        hooks: {
          postUpdate: () => this.render()
        }
      };
    }

    /**
     * Renders our React component.
     */
    render() {
      if (!this.isConnected) {
        return;
      }
      const props = this.params.props;
      const contextValues = Object.fromEntries(
      // create a new Proxy on each render so that the consumed value always triggers a prop update when passed into a PureComponent
      opts?.contexts?.map(context => {
        try {
          const value = this.getContext(context);
          if (value instanceof EmptyPanelContext) {
            return null;
          }
        } catch (_error) {
          return null;
        }
        return [context, new Proxy(this.getContext(context), {})];
      }).filter(Boolean) || []);

      // add contexts to props
      let element: React.ReactNode = React.createElement(reactComponent, {
        ...props,
        ...contextValues
      });

      // wrap component in providers
      const providers = Object.entries(contextValues).map(([context, value]) => {
        const Provider = AllowedContexts[context];
        return Provider ? React.createElement(Provider, {
          value
        }) : null;
      }).filter(Boolean);
      if (providers.length) {
        element = React.createElement(Providers, {
          providers,
          children: element
        });
      }
      // wrap with error boundary
      element = React.createElement(ReactErrorBoundary, {
        children: element,
        minimal: true
      });
      this.reactRoot.render(element);
    }
    connectedCallback(): void {
      super.connectedCallback();

      // set up the react root
      this.reactRoot = ReactDOM.createRoot(this.rootEl);
      this.render();
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      this.reactRoot.unmount();
    }
  }
  const panelTagName = `${name}-${componentId}`;
  const ReactRootEl = defineAndMakeJsxFactory<JsxProps>(panelTagName, ReactRoot);
  const Component = props => {
    const {
      sel,
      hook,
      ...rest
    } = props ?? {};
    return <ReactRootEl sel={sel} hook={hook} params={{
      props: rest
    }} data-sentry-element="ReactRootEl" data-sentry-component="Component" data-sentry-source-file="react2panel.tsx" />;
  };

  /** Accesses the Panel wrapper's tag name. ONLY rely on this for legacy use cases. */
  Component.panelTagName = panelTagName;
  return Component;
}